// The Orders screen's live push (spec/09 §6.3; F-POS-15). While the organisation's realtime socket is live, this
// subscribes to `venue:{v}:orders` and, on each `orders.created` / `orders.updated` frame — a sale placed at the
// counter or from a PC's kiosk (a food order or a pass, F-KIO-10), or one paid or cancelled — re-reads the list and
// that sale (its drawer, if open). A frame carries only the sale's id and status, so REST stays the one source
// (spec/09 §4), and on every (re)connect the list is refetched so a frame missed while disconnected converges. When
// the socket is not live (reduced motion, or disconnected) the list polls instead (`queries.ts`, `useSales`).

import { channels, ORDERS_CREATED, ORDERS_UPDATED } from '@clavian/contracts/constants';
import type { TypeId } from '@clavian/contracts/typeid';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useChannelEvents, useRealtimeLive } from '../../realtime';
import { orderKeys } from './queries';

/**
 * Keep the Orders list live from the venue's orders channel while the socket is live. Returns whether the push is
 * active, so the list can drop its polling interval.
 */
export function useOrdersLive(organizationId: string, venueId: string, enabled: boolean): boolean {
  const queryClient = useQueryClient();
  const live = useRealtimeLive() && enabled;

  useEffect(() => {
    if (!live) return;
    // Snapshot-is-recovery (spec/09 §4): resync the list whenever the socket comes (back) up.
    void queryClient.invalidateQueries({ queryKey: orderKeys.list(organizationId, venueId) });
    void queryClient.invalidateQueries({ queryKey: orderKeys.awaiting(organizationId, venueId) });
  }, [live, organizationId, venueId, queryClient]);

  useChannelEvents(
    channels.venue(venueId as TypeId<'ven'>, 'orders'),
    (envelope) => {
      if (envelope.type !== ORDERS_CREATED && envelope.type !== ORDERS_UPDATED) return;
      void queryClient.invalidateQueries({ queryKey: orderKeys.list(organizationId, venueId) });
      // The pending queue at the top of Sell (F-POS-10) follows the same frames.
      void queryClient.invalidateQueries({ queryKey: orderKeys.awaiting(organizationId, venueId) });
      const saleId = (envelope.p as { saleId?: unknown } | undefined)?.saleId;
      if (typeof saleId !== 'string') return;
      void queryClient.invalidateQueries({ queryKey: orderKeys.one(organizationId, venueId, saleId) });
      void queryClient.invalidateQueries({ queryKey: orderKeys.document(organizationId, venueId, saleId) });
    },
    live,
  );

  return live;
}
